import React, {
  CSSProperties,
  FC,
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelGroupStorage,
} from "react-resizable-panels";
import { Divider, Paper, SvgIconProps, SxProps, useTheme } from "@mui/material";
import { merge } from "lodash";
import { usePaperElevation } from "shared-ts-ui-elevation";
import { Variant } from "../Variant";
import { MainPaneProps } from "../shared/MainPaneProps";
import { SupportingPaneProvider } from "../shared/SupportingPaneProvider";
import { SupportingPaneProps } from "../shared/SupportingPaneProps";
import { PageHeader } from "../shared/PageHeader";
import { ResizeHandle } from "../shared/ResizeHandle";
import { SupportingPaneHeader } from "../shared/SupportingPaneHeader";
import { SupportingPane } from "../shared/SupportingPane";
import { useResolveResizePanelProps } from "../shared/useResolveResizePanelProps";
import { useSupportingPanel } from "../shared/useSupportingPanel";
import { usePaneSizes } from "../usePaneSizes";
import {
  PaneDimensionsProvider,
  PaneSizeProvider,
} from "shared-ts-ui-responsive";

export interface AppPagePanelGroupVProps {
  $key: string;
  name: string;
  width?: number;
  height?: number;
  variant?: Variant;
  hideName?: boolean;
  icon?: React.ComponentType<SvgIconProps>;
  children: ReactElement;
  mainPaneProps?: MainPaneProps;
  mainPaneActions?: ReactNode;
  supportingPane?: ReactElement | SupportingPaneProvider;
  supportingPaneProps?: SupportingPaneProps;
  disableResize?: boolean;
  onPanelGroupStorage: PanelGroupStorage;
}

/**
 * main pane is on top of supporting pane
 */
export const AppPagePanelGroupV: FC<AppPagePanelGroupVProps> = (props) => {
  const theme = useTheme();
  const variant = props.variant ?? "elevation";
  const disableResize = props.disableResize ?? false;
  const paperVariant = variant === "flat" ? "outlined" : variant;
  const defaultPaperElevation = usePaperElevation();
  const paneSizes = usePaneSizes();
  const supportingPanelRef = useRef<ImperativePanelHandle>(null);
  const [supportingPaneToolbarContainer, setSupportingPaneToolbarContainer] =
    useState<Element | null>(null);
  const displayHeader = props.mainPaneActions != null || !props.hideName;
  const resizePanelProps = useResolveResizePanelProps(
    "vertical",
    props.supportingPaneProps?.resizePanelProps
  );

  const bottomPanelResizePanelStyle: CSSProperties = useMemo(
    () =>
      merge({}, resizePanelProps?.style, {
        /* Remove any maxWidth to allow pane to take full width  */
        display: "flex",
        flexDirection: "column",
        paddingTop: theme.spacing(0.1),
        paddingRight: theme.spacing(variant === "flat" ? 0 : 1),
        paddingLeft: theme.spacing(variant === "flat" ? 0 : 1),
        paddingBottom: theme.spacing(variant === "flat" ? 0 : 1),
      }),
    [resizePanelProps?.style, theme, variant]
  );
  const supportingPaneSx: SxProps = useMemo(
    () =>
      merge({}, props.supportingPaneProps?.sx, {
        flexGrow: 1,
        overflowX: "auto",
        p: 1,
      }),
    [props.supportingPaneProps?.sx]
  );

  const supportingPanelProps = useSupportingPanel({
    $key: props.$key,
    supportPanelRef: supportingPanelRef,
    direction: "vertical",
    resizePanelProps: resizePanelProps,
    width: props.width,
    height: props.height,
  });

  const handleSupportingPaneToolbarContainerMount = (element: Element) => {
    setSupportingPaneToolbarContainer(element);
  };

  return (
    <PanelGroup
      direction={"vertical"}
      autoSaveId={"AppPage"}
      storage={props.onPanelGroupStorage}
    >
      <Panel
        id={`${props.$key}-main`}
        order={1}
        style={{
          display: "flex",
          flexDirection: "column",
          paddingTop: variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingRight:
            variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingLeft: variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingBottom:
            variant === "flat"
              ? theme.spacing(0)
              : variant === "elevation"
              ? theme.spacing(0.1)
              : 0,
          borderBottom:
            variant === "flat"
              ? "1px solid " + theme.palette.divider
              : undefined,
        }}
      >
        <Paper
          ref={paneSizes.mainPane.ref}
          variant={paperVariant}
          className={"MainPaneContainer MainPaneContainer-vertical"}
          elevation={
            paperVariant === "elevation"
              ? props.mainPaneProps?.paperElevation != null
                ? props.mainPaneProps?.paperElevation
                : defaultPaperElevation
              : undefined
          }
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            ...(variant === "flat" && {
              border: "unset",
              backgroundColor: "unset",
              boxShadow: "unset",
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                p: props.mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(variant === "elevation" && {
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                p: props.mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(variant === "outlined" && {
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                p: props.mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(props.mainPaneProps?.scrollableContentVertically === true && {
              paddingBottom: 0,
              ">:not(.PageHeader)": {
                overflowY: "auto",
              },
            }),
          }}
        >
          <PaneDimensionsProvider
            width={paneSizes.mainPane.width}
            height={paneSizes.mainPane.height}
          >
            <PaneSizeProvider
              width={paneSizes.mainPane.width}
              height={paneSizes.mainPane.height}
            >
              {displayHeader && (
                <>
                  <PageHeader
                    variant={variant}
                    title={props.name}
                    titleProps={{
                      sx: {
                        ...(variant === "flat" && {
                          fontSize: "1.2rem",
                          fontWeight: 500,
                        }),
                      },
                    }}
                    icon={props.icon}
                    hideTitle={props.hideName}
                  >
                    {props.mainPaneActions}
                  </PageHeader>
                  <Divider />
                </>
              )}
              {paneSizes.mainPane.width !== undefined &&
                paneSizes.mainPane.height !== undefined &&
                props.children}
            </PaneSizeProvider>
          </PaneDimensionsProvider>
        </Paper>
      </Panel>
      {props.supportingPane != null && (
        <>
          <ResizeHandle
            type={"regular"}
            orientation={"horizontal"}
            variant={variant}
            collapsed={!supportingPanelProps.supportingPaneOpen}
            label={props.supportingPaneProps?.label}
            disabled={disableResize}
            style={{
              marginTop: 0,
              marginBottom: 0,
            }}
            onOpen={supportingPanelProps.resizeHandleOnOpen}
          />
          <Panel
            id={`${props.$key}-support`}
            ref={supportingPanelRef}
            order={2}
            collapsible={true}
            collapsedSize={0}
            defaultSize={supportingPanelProps.supportingPanelDefaultSize}
            minSize={supportingPanelProps.supportingPanelMinSize}
            maxSize={supportingPanelProps.supportingPanelMaxSize}
            onCollapse={supportingPanelProps.supportingPanelOnCollapse}
            onExpand={supportingPanelProps.supportingPanelOnExpand}
            style={bottomPanelResizePanelStyle}
          >
            {props.supportingPane && props.supportingPaneProps && (
              <Paper
                className={"SupportingPaneContainer"}
                variant={paperVariant}
                elevation={
                  paperVariant === "elevation"
                    ? defaultPaperElevation
                    : undefined
                }
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: theme.spacing(0),
                  paddingLeft: theme.spacing(0),
                  paddingRight: theme.spacing(0),
                  paddingBottom: theme.spacing(0),
                  ...(variant === "flat" && {
                    border: "unset",
                    borderTop: "1px solid " + theme.palette.divider,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    backgroundColor: "unset",
                    boxShadow: "unset",
                  }),
                }}
              >
                <SupportingPaneHeader
                  layout={"vertical"}
                  label={props.supportingPaneProps?.label}
                  disableResize={disableResize}
                  onToolbarContainerMount={
                    handleSupportingPaneToolbarContainerMount
                  }
                  onChange={supportingPanelProps.supportingPanelOnToggleOpen}
                />
                <Divider />
                <SupportingPane
                  ref={paneSizes.supportingPane.ref}
                  sx={supportingPaneSx}
                  open={supportingPanelProps.supportingPaneOpen}
                  onToggleOpen={
                    supportingPanelProps.supportingPanelOnToggleOpen
                  }
                >
                  {paneSizes.supportingPane.width !== undefined &&
                    paneSizes.supportingPane.height !== undefined && (
                  <PaneDimensionsProvider
                    width={paneSizes.supportingPane.width}
                    height={paneSizes.supportingPane.height}
                  >
                    <PaneSizeProvider
                      width={paneSizes.supportingPane.width}
                      height={paneSizes.supportingPane.height}
                    >
                      {typeof props.supportingPane === "function"
                        ? supportingPaneToolbarContainer != null
                          ? props.supportingPane({
                              toolbarContainer: supportingPaneToolbarContainer,
                              layout: "PanelGroupVertical",
                            })
                          : null
                        : props.supportingPane}
                    </PaneSizeProvider>
                  </PaneDimensionsProvider>
                    )}
                </SupportingPane>
              </Paper>
            )}
          </Panel>
        </>
      )}
    </PanelGroup>
  );
};
